import React from "react";
import type { UseQueryResult } from "react-query";
import type { AxiosError } from "axios";
import memoize from "memoizee";
import { dequal } from "dequal/lite";
import debounce from "lodash/debounce";

import { User, Permissions } from "../../../models";
import {
  Member,
  MembersResponse,
  MembersDataEnvelope,
} from "../../../models/members";
import activities from "../../../redux/activities";
import { convertDataToDataByGranularId } from "../../../helpers/computations";
import { FetcherQueryKey, QueryFunction } from "../../../services/query";
import { getAuthToken } from "../../../services/granularAxios";
import { ensureValue } from "../../../services/util";
import operationsService from "../../../services/operations";
import {
  numberOfMembersListsPossiblyActiveAtTheSameTime,
  refinedListOfMembers,
  sortedListOfMembers,
  convertCurrentUserToMember,
} from "../../../services/helpers/refinements";

import type { ResultOfUseSetMembersGlobalActivityAndStatus } from "./members.globalStatus";

export type MembersFetcherParams = {
  userId: string | undefined;
  operationId: string | undefined;
  isAarEnabled: boolean | undefined;
  catalogFlag: boolean | undefined;
};
export type MembersQueryKey = FetcherQueryKey<MembersFetcherParams>;

export const membersQueryKeyPrefix = "[@fabric/core][members]";

/**
 * Computes the membersQueryKey from the given params.
 */
export const computeMembersQueryKey = (
  params: MembersFetcherParams,
): MembersQueryKey => [membersQueryKeyPrefix, params];

/**
 * Fetches the members using the operationsService.
 */
export const membersFetcher: QueryFunction<
  MembersResponse,
  MembersFetcherParams
> = async (context) => {
  const userId = ensureValue(context.queryKey[1].userId, "userId");
  const operationId = ensureValue(
    context.queryKey[1].operationId,
    "operationId",
  );
  const isAarEnabled = ensureValue(
    context.queryKey[1].isAarEnabled,
    "isAarEnabled",
  );
  const catalogFlag = ensureValue(
    context.queryKey[1].catalogFlag,
    "catalogFlag",
  );
  return operationsService.getConfirmedMembers(
    {
      userId,
      operationId,
      isAarEnabled,
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      authToken: getAuthToken(),
    },
    catalogFlag,
  );
};

/**
 * Compute the membersById derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeMembersById = memoize(convertDataToDataByGranularId, {
  max: numberOfMembersListsPossiblyActiveAtTheSameTime,
  profileName: "[members.query.helpers] computeMembersById",
});

/**
 * Compute the membersById derived data, with the currentUserAsMember
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeMembersByIdWithCurrentUserAsMember = memoize(
  (
    membersById: MembersDataEnvelope["membersByGranularId"],
    currentUserAsMember: Member,
  ) => {
    const shallowCopyOfMembersById = { ...membersById };
    shallowCopyOfMembersById[currentUserAsMember.granularId] =
      currentUserAsMember;
    return shallowCopyOfMembersById;
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName:
      "[members.query.helpers] computeMembersByIdWithCurrentUserAsMember",
  },
);

/**
 * Compute the members data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    members: MembersDataEnvelope["members"],
    membersByGranularId: MembersDataEnvelope["membersByGranularId"],
  ): MembersDataEnvelope => {
    return { members, membersByGranularId };
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName: "[members.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelopeFromResponse = memoize(
  (
    membersResponse: MembersResponse,
    currentUser: User,
    permissions: Permissions,
  ): MembersDataEnvelope => {
    // Compute the derived data for the data envelope.
    // (Don't worry. these compute functions are globally memoized.)
    const members = refinedListOfMembers(
      membersResponse.members,
      currentUser.granularId,
    );
    const membersById = computeMembersById(members);
    const currentMember = membersById[currentUser.granularId];
    if (currentMember) {
      // When we update the current user, the matching member does not always get updated
      // in the members list of the operation. This nondeterministic mess
      // means that we need to inject the current user into the members list,
      // overwriting the matching member that has possibly incorrect data.
      const currentUserAsMember = convertCurrentUserToMember(
        currentUser,
        permissions,
        // Since we cannot ascertain this information from the current user object
        // we must pass the values from the member, containing possibly incorrect data.
        currentMember.brandName,
        currentMember.cortevaType,
      );
      const membersByIdWithCurrentUserAsMember =
        computeMembersByIdWithCurrentUserAsMember(
          membersById,
          currentUserAsMember,
        );
      const sortedMembers = sortedListOfMembers(members, currentUserAsMember);
      const dataEnvelope = computeDataEnvelope(
        sortedMembers,
        membersByIdWithCurrentUserAsMember,
      );
      return dataEnvelope;
    } else {
      // At this point, the current member is not in the members list. This is most likely
      // because we have an empty array of members due to limited permissions of the current user.
      // Regardless, we will make do with what we have.
      const sortedMembers = sortedListOfMembers(members);
      const dataEnvelope = computeDataEnvelope(sortedMembers, membersById);
      return dataEnvelope;
    }
  },
  {
    max: numberOfMembersListsPossiblyActiveAtTheSameTime,
    profileName: "[members.query.helpers] computeDataEnvelopeFromResponse",
  },
);

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useMembersQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<MembersResponse, AxiosError>,
  currentUser: User | undefined,
  permissions: Permissions | undefined,
): UseQueryResult<MembersDataEnvelope, AxiosError> => {
  let dataEnvelope: MembersDataEnvelope | undefined;
  if (
    currentUser !== undefined &&
    permissions !== undefined &&
    resultOfUseQuery.data !== undefined
  ) {
    dataEnvelope = computeDataEnvelopeFromResponse(
      resultOfUseQuery.data,
      currentUser,
      permissions,
    );
  }
  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<MembersDataEnvelope, AxiosError>;
};

/**
 * Returns whether the current list of members is equal to the previous.
 *
 * This performs a full deep comparison of the members data.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useMembersQuery.
 */
export const isDataEqual = (
  prevResponse: MembersResponse | undefined,
  response: MembersResponse,
): boolean => {
  if (!prevResponse) {
    return false;
  }

  // TODO: Replace with an etag comparison once the endpoint provides it
  // consistently, correctly, and in all environments: https://granular.atlassian.net/browse/APLO-2234

  // Deep compare entire members list.
  return dequal(prevResponse.members, response.members);
};

/**
 * The returned value of this will contain
 * the React Query onSuccess function for useMembersQuery
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useMembersQuery.
 *
 * This is only meant for use in useMembersQuery.
 */
export const onSuccess = debounce(
  ({
    setMembersGlobalActivityAndStatus,
  }: {
    setMembersGlobalActivityAndStatus: ResultOfUseSetMembersGlobalActivityAndStatus;
  }) => {
    setMembersGlobalActivityAndStatus({
      status: "success",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query onError function for useMembersQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useMembersQuery.
 *
 * This is only meant for use in useMembersQuery.
 */
export const onError = debounce(
  ({
    setMembersGlobalActivityAndStatus,
  }: {
    setMembersGlobalActivityAndStatus: ResultOfUseSetMembersGlobalActivityAndStatus;
  }) => {
    setMembersGlobalActivityAndStatus({
      status: "error",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the function to call when loading in useMembersQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useMembersQuery.
 *
 * This is only meant for use in useMembersQuery.
 */
export const onLoading = debounce(
  ({
    setMembersGlobalActivityAndStatus,
  }: {
    setMembersGlobalActivityAndStatus: ResultOfUseSetMembersGlobalActivityAndStatus;
  }) => {
    setMembersGlobalActivityAndStatus({
      status: "loading",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * This hook contains the logic to run in useMembersQuery when loading.
 *
 * This hook is only meant for use in useMembersQuery.
 */
export const useHandleLoadingStatus = ({
  resultOfUseQuery,
  setMembersGlobalActivityAndStatus,
}: {
  resultOfUseQuery: UseQueryResult;
  setMembersGlobalActivityAndStatus: ResultOfUseSetMembersGlobalActivityAndStatus;
}): void => {
  const isStatusLoading = resultOfUseQuery.status === "loading";

  // Why a useEffect? So that this only sets the current operation sub data global
  // activity and status when these values change, as opposed to on
  // every render when we happen to be loading. This is a pain but is only
  // necessary to do while we preserve the behavior the deprecated data hooks.
  React.useEffect(() => {
    if (isStatusLoading) {
      onLoading({ setMembersGlobalActivityAndStatus });
    }
  }, [isStatusLoading, setMembersGlobalActivityAndStatus]);
};
